/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow

import type { Element } from 'react';

import React from 'react';
import { FormGroup, Input, Label } from 'reactstrap';

type Option = {
  value: string,
  label: string,
  group?: string,
};

type Props = {|
  input: {
    name: string,
    value: Array<string> | string,
    onChange: Function,
  },
  options?: Array<Option>,
  disabled?: boolean,
  // When true, the bound array holds the EXCLUDED option values, so a box is
  // checked when its value is NOT in the array (empty array => all checked).
  // When false (default), the array holds the checked/selected values as usual.
  valuesAreExcluded?: boolean,
|};

const groupOptions = (options: Array<Option>): Array<{ group: string, options: Array<Option> }> => {
  const order = [];
  const byGroup = {};
  options.forEach(option => {
    const key = option.group || '';
    if (!byGroup[key]) {
      byGroup[key] = [];
      order.push(key);
    }
    byGroup[key].push(option);
  });
  return order.map(group => ({ group, options: byGroup[group] }));
};

const CheckboxGroup = ({
  input,
  options = [],
  disabled = false,
  valuesAreExcluded = false,
}: Props): Element<'div'> => {
  const { name, value, onChange } = input;
  const selection = Array.isArray(value) ? value : [];
  const safeOptions = Array.isArray(options) ? options : [];

  const toggle = optionValue => {
    const next = selection.includes(optionValue)
      ? selection.filter(v => v !== optionValue)
      : [...selection, optionValue];
    onChange(next);
  };

  const isChecked = optionValue =>
    valuesAreExcluded ? !selection.includes(optionValue) : selection.includes(optionValue);

  return (
    <div>
      {groupOptions(safeOptions).map(({ group, options: groupedOptions }) => (
        <fieldset key={group || 'default'}>
          {group && <legend>{group}</legend>}
          {groupedOptions.map(option => {
            const id = `${name}-${option.value}`;
            return (
              <FormGroup check className="mb-2" key={option.value}>
                <Label check htmlFor={id}>
                  <Input
                    id={id}
                    type="checkbox"
                    checked={isChecked(option.value)}
                    disabled={disabled}
                    onChange={() => toggle(option.value)}
                  />{' '}
                  {option.label}
                </Label>
              </FormGroup>
            );
          })}
        </fieldset>
      ))}
    </div>
  );
};

export default CheckboxGroup;
